import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import DataTable from "../../../components/Common/DataTable";
import {
  getDataTableQuery,
  getUserProfile,
  handleFilters,
} from "../../../helpers/functions_helper";
import service from "../services/service";
import { Link, useLocation } from "react-router-dom";
import { renderRowHTML } from "../../../utils/utils";
import { dateFormat, formatCurrency } from "../../../utils/formats";
import { Button } from "reactstrap";
import Drawer from "../../../components/Drawer";
import Filters from "../Filters";

const renderEmployeeCell = (rowContent: any, row: any) => {
  const name =
    row.employee_name ||
    (row.employee ? `${row.employee.first_name} ${row.employee.last_name}`.trim() : "");
  const initials = name
    ? name
        .split(" ")
        .filter((part: string) => part)
        .map((part: string) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?";
  const assignment = row.assignment_type?.assignment_type_value || "";

  return (
    <div className="d-flex align-items-center">
      <div
        className="rounded-circle text-white d-flex align-items-center justify-content-center me-3"
        style={{
          width: 40,
          height: 40,
          backgroundColor: "#564ab1",
          fontWeight: 700,
          fontSize: "0.9rem",
        }}
      >
        {initials}
      </div>
      <div>
        <div className="fw-semibold">{name || "-"}</div>
        <div className="text-muted small">{assignment || "-"}</div>
      </div>
    </div>
  );
};

const List = (props: any) => {
  const { t } = useTranslation();
  const location = useLocation();
  const moduleAccess = getUserProfile("immigration");
  const savedfilters = handleFilters("GET");
  const [loadingListData, setLoadingListData] = useState(true);
  const [listData, setListData] = useState([]);
  const [openFilter, setOpenFilter] = useState<boolean>(false);

  const columns = [
    {
      dataField: "DT_RowIndex",
      text: t("Sr. No."),
      sort: false,
      search: false,
      classes: "fix-col-left",
      headerClasses: "fix-col-left",
      headerStyle: { width: "155px" },
      style: { width: "155px" },
    },
    {
      dataField: "employee_name",
      text: t("Employee"),
      sort: true,
      search: true,
      formatter: (cell: any, row: any) => renderEmployeeCell(cell, row),
    },
    {
      dataField: "nationality",
      text: t("Nationality"),
      sort: true,
      search: true,
      formatter: (rowContent: any, row: any) => row.nationality?.name || "-",
    },
    {
      dataField: "host_country",
      text: t("Host Country"),
      sort: true,
      search: true,
      formatter: (rowContent: any, row: any) => row.host_country?.name || "-",
    },
    // {
    //   dataField: "assignment_type_id",
    //   text: t("Assignment Type"),
    //   sort: true,
    //   search: true,
    //   formatter: (rowContent: any, row: any) => {
    //     return row.assignment_type_id ? (
    //       <span className="badge_custome badge-soft-info badge bg-info rounded-pill">
    //         {row.assignment_type?.assignment_type_value}
    //       </span>
    //     ) : (
    //       "-"
    //     );
    //   },
    // },
    {
      dataField: "immigration_status_id",
      text: t("Status"),
      sort: true,
      search: true,
      formatter: (rowContent: any, row: any) => {
        return row.immigration_status_id ? (
          <span className="badge_custome badge-soft-primary badge bg-primary rounded-pill">
            {row.immigration_status?.immigration_status_value}
          </span>
        ) : (
          "-"
        );
      },
    },
    {
      dataField: "action",
      text: t("Action"),
      sort: false,
      search: false,
      formatter: (rowContent: any, row: any): any => {
        return (
          <div className="d-flex gap-2 text-end">
            {row?.applications && row?.applications?.length > 0 ? (
              <Link
                className="btn btn-pill btn-outline-secondary btn-sm"
                to={`/immigration/view-immigration/${row.id}/${row.immigration_id}`}
              >
                <span className="align-middle material-symbols-outlined fw-light fs-5 me-1">
                  edit
                </span>
                {t("View Applications")}
              </Link>
            ) : moduleAccess.CAN_CREATE ? (
              <Link
                className="btn btn-pill btn-outline-primary btn-sm"
                to={`/immigration/create-application/${row.id}/${row.immigration_id}`}
              >
                <span className="align-middle material-symbols-outlined fw-light fs-5 me-1">
                  add
                </span>
                {t("Create Application")}
              </Link>
            ) : null}
          </div>
        );
      },
    },
  ];

  const [datatableOptions, setDatatableOptions] = useState(
    savedfilters && savedfilters?.datatableOptions
      ? (savedfilters?.datatableOptions as any)
      : {
          draw: 1,
          page: 1,
          recordsTotal: 0,
          recordsFiltered: 0,
          sizePerPage: 20,
          searchText: "",
          sortField: "id",
          sortOrder: "DESC",
        },
  );
  const [filters, setFilters] = useState(
    savedfilters && savedfilters?.filters
      ? (savedfilters?.filters as any)
      : {
          is_filtered: false,
        },
  );

  const onTableChange = (type: any, newState: any) => {
    let draw = datatableOptions.draw + 1;
    let page = 1;
    if (type === "search") {
      page = 1;
    } else {
      page = newState.page;
    }
    const updatedDatatableOptions = {
      ...datatableOptions,
      draw: draw,
      page: page,
      sizePerPage: newState.sizePerPage,
      searchText: newState.searchText,
      sortField: newState.sortField,
      sortOrder: newState.sortOrder,
    };
    setDatatableOptions(updatedDatatableOptions);
    setListData([]);
    setLoadingListData(true);
  };

  const clearFilter = () => {
    const updatedDatatableOptions = {
      ...datatableOptions,
      draw: 1,
      page: 1,
      sizePerPage: 20,
      searchText: "",
      sortField: "id",
      sortOrder: "DESC",
    };
    setDatatableOptions(updatedDatatableOptions);

    const filter = {
      ...filters,
      is_filtered: false,
    };
    setFilters(filter);

    setListData([]);
    setLoadingListData(true);
  };

  const loadingDatatable = async () => {
    try {
      const res = (await service.getAll({
        ...getDataTableQuery(datatableOptions, columns),
        filters,
      })) as any;

      if (res.data.status === "success") {
        setListData(res.data.data);

        const updatedDatatableOptions = {
          ...datatableOptions,
          recordsTotal: res.data.recordsTotal,
          recordsFiltered: res.data.recordsFiltered,
        };
        setDatatableOptions(updatedDatatableOptions);
        setLoadingListData(false);
      } else {
        setListData([]);

        const updatedDatatableOptions = {
          ...datatableOptions,
          recordsTotal: 0,
          recordsFiltered: 0,
        };
        setDatatableOptions(updatedDatatableOptions);
        setLoadingListData(false);
      }
    } catch (err) {
      setListData([]);

      const updatedDatatableOptions = {
        ...datatableOptions,
        recordsTotal: 0,
        recordsFiltered: 0,
      };
      setDatatableOptions(updatedDatatableOptions);
      setLoadingListData(false);
    }
  };

  useEffect(() => {
    if (loadingListData === true) {
      loadingDatatable();
    }
  }, [loadingListData]);

  // Filters -------------------------------------------------
  const handleFiltersChange = (obj: any) => {
    setFilters((state: any) => ({ ...state, ...obj }));
  };

  const handleApplyFilters = () => {
    setListData([]);
    setLoadingListData(true);
    setOpenFilter(false);
  };
  // Filters -------------------------------------------------

  return (
    <React.Fragment>
      <DataTable
        columns={columns}
        products={listData}
        datatableOptions={datatableOptions}
        filters={filters}
        onTableChange={onTableChange}
        loadingListData={loadingListData}
        clearFilter={clearFilter}
        settingTableName="task_table"
        slug="all-tasks"
        customButtons={
          <>
            {moduleAccess.IS_INTERNAL ? (
              <Button
                title={t("Filter")}
                outline={filters.is_filtered === true ? false : true}
                color={filters.is_filtered === true ? "secondary" : "light"}
                onClick={() => setOpenFilter((state) => !state)}
              >
                {filters.is_filtered === false ? (
                  <span className="align-middle material-symbols-outlined fw-light fs-4">
                    filter_alt
                  </span>
                ) : (
                  <span className="align-middle material-symbols-outlined fw-light fs-4">
                    filter_alt_off
                  </span>
                )}
              </Button>
            ) : null}
          </>
        }
      />
      <Drawer
        title={t("Filters")}
        open={openFilter}
        onClose={() => setOpenFilter((state) => !state)}
        onReset={clearFilter}
        footer={
          <Button color="primary" style={{ margin: "16px" }} onClick={handleApplyFilters}>
            {t("Apply")}
          </Button>
        }
      >
        <Filters
          filters={filters}
          onApply={() => {}}
          onClear={() => {}}
          onChange={(obj: any) => handleFiltersChange(obj)}
        />
      </Drawer>
    </React.Fragment>
  );
};

export default List;
